/**
 * AiAutocomplete — a Tiptap/ProseMirror extension that renders an inline
 * "ghost text" completion after the caret (Gmail Smart Compose / Superhuman
 * type-ahead), Jul 2026.
 *
 * The extension owns only the RENDERING + accept/dismiss keymap. The async
 * suggestion fetch (debounced network call) lives in the React host
 * ({@link RichEmailBody}) which pushes the resolved text in via the
 * ``setAiSuggestion`` command. This keeps the extension pure and testable and
 * lets ``@ando/ui`` stay free of any network transport.
 *
 * Behaviour:
 *   - ``setAiSuggestion(text)`` shows ``text`` as a muted widget at the caret.
 *   - ``Tab`` (while a suggestion shows) inserts it and clears — SOTA accept.
 *   - ``Escape`` dismisses without inserting.
 *   - ANY doc edit or caret move clears the suggestion (it's stale the instant
 *     the context changes), so it can never be inserted at the wrong place.
 */

import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

interface GhostState {
  readonly suggestion: string;
  /** Doc position the suggestion is anchored to (the caret when it was set). */
  readonly pos: number;
}

export const aiAutocompleteKey = new PluginKey<GhostState | null>("aiAutocomplete");

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    aiAutocomplete: {
      /** Show ``suggestion`` as ghost text at the caret (empty clears). */
      setAiSuggestion: (suggestion: string) => ReturnType;
      /** Dismiss any showing suggestion without inserting. */
      clearAiSuggestion: () => ReturnType;
      /** Insert the showing suggestion at its anchor and clear. */
      acceptAiSuggestion: () => ReturnType;
    };
  }
}

export const AiAutocomplete = Extension.create({
  name: "aiAutocomplete",

  addCommands() {
    return {
      setAiSuggestion:
        (suggestion: string) =>
        ({ state, dispatch }) => {
          if (dispatch) {
            const next: GhostState | null = suggestion
              ? { suggestion, pos: state.selection.head }
              : null;
            dispatch(state.tr.setMeta(aiAutocompleteKey, next));
          }
          return true;
        },
      clearAiSuggestion:
        () =>
        ({ state, dispatch }) => {
          if (dispatch) dispatch(state.tr.setMeta(aiAutocompleteKey, null));
          return true;
        },
      acceptAiSuggestion:
        () =>
        ({ state, dispatch }) => {
          const cur = aiAutocompleteKey.getState(state);
          if (!cur || !cur.suggestion) return false;
          if (dispatch) {
            dispatch(
              state.tr.insertText(cur.suggestion, cur.pos).setMeta(aiAutocompleteKey, null),
            );
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<GhostState | null>({
        key: aiAutocompleteKey,
        state: {
          init: () => null,
          apply(tr, value) {
            const meta = tr.getMeta(aiAutocompleteKey) as GhostState | null | undefined;
            if (meta !== undefined) return meta; // explicit set / clear
            if (!value) return null;
            // Any content edit or caret move makes the suggestion stale.
            if (tr.docChanged || tr.selectionSet) return null;
            return value;
          },
        },
        props: {
          decorations(state) {
            const cur = aiAutocompleteKey.getState(state);
            if (!cur || !cur.suggestion) return DecorationSet.empty;
            const widget = Decoration.widget(
              cur.pos,
              () => {
                const span = document.createElement("span");
                span.textContent = cur.suggestion;
                span.setAttribute("aria-hidden", "true");
                span.style.color = "#9A9893"; // ink-3 — muted ghost
                span.style.pointerEvents = "none";
                span.style.whiteSpace = "pre-wrap";
                return span;
              },
              { side: 1, marks: [] },
            );
            return DecorationSet.create(state.doc, [widget]);
          },
          handleKeyDown(view, event) {
            const cur = aiAutocompleteKey.getState(view.state);
            if (!cur || !cur.suggestion) return false;
            if (event.key === "Tab") {
              event.preventDefault();
              view.dispatch(
                view.state.tr.insertText(cur.suggestion, cur.pos).setMeta(aiAutocompleteKey, null),
              );
              return true;
            }
            if (event.key === "Escape") {
              view.dispatch(view.state.tr.setMeta(aiAutocompleteKey, null));
              return true;
            }
            return false;
          },
        },
      }),
    ];
  },
});
